import Vue from 'vue'

export const REPORT_CONTENT_ICON_PREFIX = '/delta/dist/report_contents/svg/'
export const REPORT_SECTIONS_ICONS = {
  'section-remarks-reworks': 'remarks-reworks.svg',
  'section-remarks': 'remarks.svg',
  'section-service-object': 'service-object.svg',
  'section-object-photos': 'object-photos.svg',
  'section-contract': 'contract.svg',
  'section-initiator-active-requests': 'initiator-active-requests.svg',
  'section-executor': 'executor.svg',
  'section-forklift-trucks': 'forklift.svg',
  'section-address': 'address.svg',
  'section-initiator-requests': 'initiator-requests.svg',
  'section-active-requests': 'initiator-active-requests-contents.svg',
  'section-audit-remarks': 'audit-remarks.svg',
  'service-object-maintenance-notes': 'service-object-maintenance-notes.svg',
  'section-photos': 'photos.svg',
  'section-docs': 'docs.svg',
  'section-applicant-description': 'applicant-description.svg',
  'section-checklist': 'checklist.svg',
  'section-history': 'history.svg',
  'section-reworks': 'reworks.svg',
  'section-general-comment': 'general-comment.svg',
  'section-materials': 'materials.svg',
  'section-materials-available': 'materials-available.svg',
  'section-brigade': 'brigade.svg',
  'section-vehicles': 'vehicles.svg',
  'section-stocks': 'stocks.svg',
  'section-brigade-employees': 'brigade-employees.svg',
  'report-photos-mode': 'photos-mode.svg',
  'section-materials-workshift-demountings': 'workshift-demountings.svg',
  'section-materials-demountings': 'demountings.svg',
  'section-materials-debits': 'debits.svg',
  'section-materials-mountings': 'mountings.svg',
  'section-materials-consumptions': 'consumptions.svg',
  'report-doc-category-dispatcher': 'docs-dispatcher.svg',
  'report-doc-category-afterWorkExecution': 'docs-afterWorkExecution.svg',
  'report-photos-category-dispatcher': 'photo-dispatcher.svg',
  'report-photos-category-vehicle': 'photo-vehicle.svg',
  'report-photos-category-video': 'photo-video.svg',
  'report-photos-category-beforeWorkExecution': 'photo-beforeWorkExecution.svg',
  'report-photos-category-duringWorkExecution': 'photo-duringWorkExecution.svg',
  'report-photos-category-afterWorkExecution': 'photo-afterWorkExecution.svg',
  'report-photos-category-audit': 'audit-remarks.svg',
  'report-photos-category-advertisements': 'photo-advertisements.svg',
  'report-photos-category-forAuditRequest': 'photo-forAuditRequest.svg'
}

function persistSessionState (state) {
  if (!state.sessionStorageKey || typeof window === 'undefined') {
    return
  }
  try {
    window.sessionStorage.setItem(state.sessionStorageKey, JSON.stringify({
      activeSectionId: state.activeSectionId,
      expandedSectionsIds: state.expandedSectionsIds,
      scrollTop: state.scrollTop
    }))
  } catch (e) {

  }
}

function getChildNodes (contentsMap, parentId) {
  return Object.keys(contentsMap)
    .map(id => contentsMap[id])
    .filter(node => node && node.id && (node.parentId || null) === (parentId || null) && node.id !== (parentId || null))
    .sort((a, b) => a.weight - b.weight)
}

function buildContentsTree (contentsMap, parentId, visited) {
  const seen = visited || Object.create(null)
  return getChildNodes(contentsMap, parentId).reduce((nodes, node) => {
    if (seen[node.id]) {
      return nodes
    }
    seen[node.id] = true
    nodes.push({
      id: node.id,
      parentId: node.parentId,
      weight: node.weight,
      title: node.title,
      icon: node.icon,
      groupingOnly: node.groupingOnly,
      children: buildContentsTree(contentsMap, node.id, seen)
    })
    return nodes
  }, [])
}

function filterVisibleContentsTree (nodes) {
  const result = []
  for (const node of nodes) {
    const children = filterVisibleContentsTree(node.children || [])
    if (node.groupingOnly && children.length === 0) {
      continue
    }
    result.push(Object.assign({}, node, { children }))
  }
  return result
}

function hasDescendantWithId (contentsMap, parentId, targetId, visited) {
  const seen = visited || Object.create(null)
  if (!parentId || seen[parentId]) {
    return false
  }
  seen[parentId] = true
  const children = getChildNodes(contentsMap, parentId)
  for (const child of children) {
    if (child.id === targetId || hasDescendantWithId(contentsMap, child.id, targetId, seen)) {
      return true
    }
  }
  return false
}

function getExpandableSectionIds (contentsMap) {
  return Object.keys(contentsMap).filter(id => getChildNodes(contentsMap, id).length > 0)
}

export function createDocumentContentStore ({ iconUrlPrefix, sectionsIcons, storageKeyPrefix }) {
  function createState () {
    return {
      contentsMap: {},
      activeSectionId: null,
      expandedSectionsIds: [],
      scrollTop: 0,
      sessionStorageKey: null,
      instancesCount: 0,
      iconUrlPrefix: iconUrlPrefix,
      sectionsIcons: sectionsIcons
    }
  }

  return {
    namespaced: true,
    state: createState,
    getters: {
      getContentsTree: state => filterVisibleContentsTree(buildContentsTree(state.contentsMap, null)),
      getActiveSectionId: state => state.activeSectionId,
      getExpandedSectionsIds: state => state.expandedSectionsIds,
      getScrollTop: state => state.scrollTop,
      areAllSectionsExpanded: state => {
        const expandableIds = getExpandableSectionIds(state.contentsMap)
        return expandableIds.length > 0 && expandableIds.every(id => state.expandedSectionsIds.includes(id))
      },
      hasActiveDescendant: state => id => {
        if (!state.activeSectionId) {
          return false
        }
        return hasDescendantWithId(state.contentsMap, id, state.activeSectionId)
      },
      getSectionIcon: state => id => {
        const icon = state.sectionsIcons[id]
        return icon ? state.iconUrlPrefix + icon : null
      }
    },
    actions: {
      retainInstance (context) {
        context.commit('retainInstance')
      },
      releaseInstance (context) {
        context.commit('releaseInstance')
        return context.state.instancesCount
      },
      initializeSession (context, documentId) {
        const storageKey = `${storageKeyPrefix}.${documentId || 'unknown'}`
        let savedState = null
        if (typeof window !== 'undefined') {
          try {
            if (window.sessionStorage) {
              savedState = JSON.parse(window.sessionStorage.getItem(storageKey))
            }
          } catch (e) {
            savedState = null
          }
        }
        context.commit('initializeSession', { storageKey, savedState })
      },
      registerContentSection (context, payload) {
        context.commit('registerContentSection', payload)
      },
      unregisterContentSection (context, id) {
        context.commit('unregisterContentSection', id)
      },
      setActiveSection (context, id) {
        context.commit('setActiveSection', id)
        persistSessionState(context.state)
      },
      setNodeExpanded (context, payload) {
        context.commit('setNodeExpanded', payload)
        persistSessionState(context.state)
      },
      setAllExpanded (context, expand) {
        context.commit('setAllExpanded', expand)
        persistSessionState(context.state)
      },
      setScrollTop (context, scrollTop) {
        context.commit('setScrollTop', scrollTop)
        persistSessionState(context.state)
      }
    },
    mutations: {
      retainInstance (state) {
        state.instancesCount += 1
      },
      releaseInstance (state) {
        state.instancesCount = Math.max(0, state.instancesCount - 1)
      },
      initializeSession (state, { storageKey, savedState }) {
        state.sessionStorageKey = storageKey
        state.activeSectionId = savedState && savedState.activeSectionId || null
        state.expandedSectionsIds = savedState && Array.isArray(savedState.expandedSectionsIds)
          ? savedState.expandedSectionsIds.slice()
          : []
        state.scrollTop = savedState && typeof savedState.scrollTop === 'number' ? savedState.scrollTop : 0
      },
      registerContentSection (state, payload) {
        const { id, parentId, weight, title, icon, groupingOnly } = payload
        const node = {
          id,
          parentId: parentId || null,
          weight,
          title,
          icon: icon || null,
          groupingOnly: !!groupingOnly
        }
        Vue.set(state.contentsMap, id, node)
      },
      unregisterContentSection (state, id) {
        if (!state.contentsMap[id]) {
          return
        }
        Vue.delete(state.contentsMap, id)
      },
      setActiveSection (state, id) {
        state.activeSectionId = id
      },
      setNodeExpanded (state, { id, expanded }) {
        const index = state.expandedSectionsIds.indexOf(id)
        if (expanded && index === -1) {
          state.expandedSectionsIds.push(id)
        } else if (!expanded && index !== -1) {
          state.expandedSectionsIds.splice(index, 1)
        }
      },
      setAllExpanded (state, expand) {
        if (expand) {
          state.expandedSectionsIds = getExpandableSectionIds(state.contentsMap)
        } else {
          state.expandedSectionsIds = []
        }
      },
      setScrollTop (state, scrollTop) {
        state.scrollTop = typeof scrollTop === 'number' && scrollTop > 0 ? scrollTop : 0
      }
    }
  }
}

export default createDocumentContentStore({
  iconUrlPrefix: REPORT_CONTENT_ICON_PREFIX,
  sectionsIcons: REPORT_SECTIONS_ICONS,
  storageKeyPrefix: 'delta.reportContent'
})
